import { useTranslation } from "react-i18next";
import { useEffect, useState, useRef } from "react";
import { usePathname, useRouter } from "next/navigation";
import { createLogger } from "@/app/lib/logger";
import { loadTranslations } from "@/app/utils/i18next/load";
import {
  SUPPORTED_LOCALES,
  LOCALE_LABELS,
  type SupportedLocale,
  getLocaleFromPath,
  addLocaleToPath,
  isLocaleExcludedPath,
  isValidLocale,
} from "@/app/lib/i18n-config";

const log = createLogger("LanguageSwitcher");

function setLocaleCookie(locale: string) {
  document.cookie = `preferred-locale=${locale};path=/;max-age=${60 * 60 * 24 * 365};samesite=lax`;
}

// Navbar look: a ghost glass pill with a strong glass popover of pill rows,
// so the menu text reads over anything. Neither sets its own text colour:
// both take the glass ink, which the navbar's dark bar turns light, and the
// row hover is a tint that reads on light and dark glass alike. Both menus
// cap their height and scroll, since the navbar is fixed and the full
// language list is taller than most screens.
const PILL_TRIGGER = "btn-glass-ghost h-9 gap-1 px-3 text-xs font-medium";
const PILL_MENU =
  "glass-strong absolute right-0 z-50 mt-2 max-h-[60vh] min-w-[140px] overflow-y-auto animate-orbit-fade rounded-[1.25rem] p-1.5 motion-reduce:animate-none";
const PILL_ITEM =
  "flex w-full items-center gap-2 rounded-full px-3 py-1.5 text-left text-xs transition-colors duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-signal-accent/10";

interface LanguageSwitcherProps {
  /** "pill" matches the site navbar's shapes; the default suits embedded pages. */
  variant?: "default" | "pill";
  /** Open the menu above the trigger, for a switcher at the bottom of a panel. */
  dropUp?: boolean;
}

export default function LanguageSwitcher({
  variant = "default",
  dropUp = false,
}: LanguageSwitcherProps = {}) {
  const isPill = variant === "pill";
  const { i18n } = useTranslation();
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    setMounted(true);
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Blog and job pages use their own locale (not path-based), so skip navigation
  const isExcludedFromLocaleRouting = isLocaleExcludedPath(pathname);

  const changeLanguage = async (lng: SupportedLocale) => {
    try {
      // Load the translation bundle if it doesn't exist
      if (!i18n.hasResourceBundle(lng, "translation")) {
        const translation = await loadTranslations(lng);
        i18n.addResourceBundle(lng, "translation", translation, true, true);
      }

      // Now change the language - this will trigger re-renders
      await i18n.changeLanguage(lng);

      // Save to cookie for middleware detection
      setLocaleCookie(lng);

      setOpen(false);

      // On blog/job pages, just change i18n in place (no locale prefix in URL)
      if (!isExcludedFromLocaleRouting) {
        const newPath = addLocaleToPath(pathname, lng);
        router.push(newPath);
      }
    } catch (error) {
      log.error("Failed to change language", error, { target_language: lng });
    }
  };

  if (!mounted) {
    return null;
  }

  const languages = SUPPORTED_LOCALES.map(code => ({
    code,
    label: LOCALE_LABELS[code].label,
    icon: LOCALE_LABELS[code].flag,
  }));

  // Blog/job URLs carry no locale; those pages set i18n from their content
  const currentLocale =
    isExcludedFromLocaleRouting && isValidLocale(i18n.language)
      ? i18n.language
      : getLocaleFromPath(pathname);
  const current = languages.find(l => l.code === currentLocale) || languages[0];

  return (
    <div ref={dropdownRef} className="relative inline-block text-left">
      <button
        type="button"
        className={
          isPill
            ? PILL_TRIGGER
            : "px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded transition-colors flex items-center gap-1"
        }
        aria-expanded={open}
        onClick={() => setOpen(prev => !prev)}
        // Starts with the visible code so the name matches what is shown
        aria-label={`${current.code.toUpperCase()}, Change language`}
      >
        <span className="uppercase">{current.code}</span>
        <svg
          className={`w-3 h-3 transition-transform ${isPill ? "duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]" : ""} ${open ? "rotate-180" : ""}`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" />
        </svg>
      </button>
      {open && (
        <div
          className={`${
            isPill
              ? PILL_MENU
              : "absolute right-0 z-50 mt-1 max-h-[60vh] min-w-[100px] overflow-y-auto bg-white border border-gray-200 rounded shadow-lg py-1"
          }${dropUp ? " bottom-full mb-2" : ""}`}
        >
          {languages
            .filter(l => l.code !== current.code)
            .map(l => (
              <button
                key={l.code}
                type="button"
                className={
                  isPill
                    ? PILL_ITEM
                    : "w-full px-3 py-1.5 text-xs text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
                }
                onClick={() => changeLanguage(l.code as SupportedLocale)}
              >
                <span>{l.icon}</span>
                <span>{l.label}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
